import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, LogOut, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function ChangePassword({ forced = false }) {
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [oldp, setOldp] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (n1.length < 8) { setError("Kata sandi baru minimal 8 karakter."); return; }
    if (n1 !== n2) { setError("Konfirmasi kata sandi tidak cocok."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", { old_password: oldp, new_password: n1 });
      setUser(data);
      toast.success("Kata sandi berhasil diubah.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || "Gagal mengubah kata sandi.";
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const formCard = (
    <Card className="w-full max-w-md rounded-2xl p-6" data-testid="change-password-card">
      {forced && (
        <div className="mb-4 flex items-start gap-3 rounded-xl bg-amber-500/10 p-3 text-amber-700 dark:text-amber-400">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-sm">Demi keamanan, Anda wajib mengganti kata sandi sementara sebelum melanjutkan.</p>
        </div>
      )}
      <div className="mb-4 flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-primary" />
        <h1 className="font-heading text-xl font-bold">Ganti Kata Sandi</h1>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>{forced ? "Kata Sandi Sementara" : "Kata Sandi Lama"}</Label>
          <PasswordInput value={oldp} onChange={(e) => setOldp(e.target.value)} required className="rounded-xl" data-testid="change-old-password-input" /></div>
        <div className="space-y-1.5"><Label>Kata Sandi Baru</Label>
          <PasswordInput value={n1} onChange={(e) => setN1(e.target.value)} required className="rounded-xl" placeholder="min. 8 karakter" data-testid="change-new-password-input" /></div>
        <div className="space-y-1.5"><Label>Ulangi Kata Sandi Baru</Label>
          <PasswordInput value={n2} onChange={(e) => setN2(e.target.value)} required className="rounded-xl" data-testid="change-confirm-password-input" /></div>
        {error && <p className="text-sm text-destructive" data-testid="change-password-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-xl min-h-[44px]" data-testid="change-password-submit">
          {loading ? "Menyimpan..." : "Simpan Kata Sandi Baru"}
        </Button>
      </form>
      {forced && (
        <button onClick={doLogout} className="mt-4 flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-destructive" data-testid="change-password-logout">
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      )}
    </Card>
  );

  if (forced) {
    return <div className="flex min-h-screen items-center justify-center bg-background p-4">{formCard}</div>;
  }
  return <div className="mx-auto max-w-md">{formCard}</div>;
}
